import React, { useState, useRef } from 'react';
import { Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MessageInput = ({ onSendMessage, disabled = false, placeholder }) => {
  const { t } = useTranslation(['events']);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const textareaRef = useRef(null);
  
  const defaultPlaceholder = placeholder || t('chat.type_message');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim() || isSending || disabled) return;
    
    setIsSending(true);
    try {
      await onSendMessage(message.trim());
      setMessage('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleTextareaChange = (e) => {
    setMessage(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, 120); // Max height of ~6 lines
    textarea.style.height = `${newHeight}px`;
  };

  if (disabled) {
    return (
      <div className="p-4 bg-gray-50 border-t">
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
          <span className="text-red-600 text-sm font-medium">
            {t('chat.revoked_event_notice')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 bg-white border-t border-gray-200">
      <form onSubmit={handleSubmit} className="flex items-end space-x-2">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder={defaultPlaceholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent max-h-[80px] min-h-[36px] text-sm"
            style={{ height: '36px' }}
            disabled={isSending}
          />
        </div>
        <button
          type="submit"
          disabled={!message.trim() || isSending}
          className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
        >
          {isSending ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Send size={16} />
          )}
        </button>
      </form>
    </div>
  );
};

export default MessageInput;
